import { useQuery } from '@apollo/client';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { InputBase, Typography, useTheme } from '@mui/material';

import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useNavigate } from '@tanstack/react-router';
import _ from 'lodash';
import React, { type ChangeEvent, useState } from 'react';
import { AddButton } from 'src/Administration/Common';
import allFieldMapsConfigQuery from 'src/api/admin/allFieldMapsConfigQuery';
import FieldMapsDeleteModal from 'src/components/Modals/FieldMapsDeleteModal';

import type { AllFieldMapConfigsData, FieldMapConfig } from '../types';
import MetadataFieldMapsItem from './MetadataFieldMapsItem';
import styles from './MetadataFieldMapsStyles';

interface SortChoice {
  choice: string;
  ascending: boolean;
  active: boolean;
}

const MetadataFieldMapsComponent: React.FC = () => {
  const classes = styles();
  const theme = useTheme();
  const navigate = useNavigate();

  const [searchVal, setSearchVal] = useState<string>('');
  const [sortChoice, setSortChoice] = useState<SortChoice>({ choice: 'name', ascending: true, active: true });

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchVal(e.target.value);
  };

  const { data: allFieldMapsData } = useQuery<AllFieldMapConfigsData>(allFieldMapsConfigQuery, {
    fetchPolicy: 'network-only',
  });

  if (!allFieldMapsData) return null;

  const allFieldsNode: FieldMapConfig[] = allFieldMapsData?.allFieldMapConfigs.edges.map((item) => item.node);
  const allFieldsNodeFilteredBySearch = allFieldsNode.filter((a) =>
    a.name.toUpperCase().includes(searchVal.toUpperCase()),
  );

  let sortedFilteredAllFieldMaps: FieldMapConfig[];
  if (sortChoice.ascending === true) {
    sortedFilteredAllFieldMaps = _.sortBy(allFieldsNodeFilteredBySearch, (item) => item.name);
  } else if (sortChoice.ascending === false) {
    sortedFilteredAllFieldMaps = _.sortBy(allFieldsNodeFilteredBySearch, (item) => item.name).reverse();
  } else {
    sortedFilteredAllFieldMaps = allFieldsNodeFilteredBySearch;
  }

  const nameSortHandler = (): void => {
    setSortChoice({ choice: 'name', ascending: !sortChoice.ascending, active: true });
  };

  const gotoAddFieldMapsPage = (): void => {
    navigate({ to: '/systemadmin/metadata/field-maps/new' });
  };

  const TableHeaders: React.FC = () => {
    return (
      <React.Fragment>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Name</Typography>
            <IconButton onClick={nameSortHandler} style={{ margin: '0px 10px 0px 10px', padding: '0px' }} size="large">
              {sortChoice.ascending === true ? (
                <ArrowUpwardIcon color={sortChoice.active === true ? 'inherit' : 'disabled'} />
              ) : (
                <ArrowDownwardIcon color={sortChoice.active === true ? 'inherit' : 'disabled'} />
              )}
            </IconButton>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography></Typography>
          </div>
        </TableCell>
      </React.Fragment>
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.searchAndButton}>
        <div className={classes.addFieldButton}>
          <AddButton onClick={gotoAddFieldMapsPage}>Add a Field Map</AddButton>
        </div>
        <div className={classes.fieldSearchBox}>
          <InputBase
            className={classes.searchInput}
            placeholder={'Quick Search'}
            value={searchVal}
            onChange={onChangeSearch}
          />
        </div>
      </div>
      <TableContainer
        style={{ width: '98%', margin: '20px 20px 30px 20px', maxHeight: '800px' }}
        className={classes.tableContainer}
      >
        <Table className={classes.fieldMapTable} stickyHeader>
          <TableHead>
            <TableRow>
              <TableHeaders />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFilteredAllFieldMaps.map((item) => {
              return <MetadataFieldMapsItem key={item.id} item={item} />;
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <FieldMapsDeleteModal />
    </div>
  );
};

export default MetadataFieldMapsComponent;
